/**
 * LEADS-WORKSPACE-ROLE-SCOPED-01 -- pure helpers for the canonical leads
 * list (`/dashboard/leads`).
 *
 * CRM-LIST-VIEWS-V1 (V1-P7) RETIREMENT NOTE: the v1 CLIENT-side system
 * views, their predicates (isNewLead / hasLoggedContact / hasPhoneOnFile /
 * isGooglePaid / isMyLead / isUnassignedLead), the client sort table, and
 * the page-completeness sort gate were SUPERSEDED when filtering/sorting
 * moved SERVER-side (the C-5 one-predicate-one-home law):
 *   - stage / assignee / SLA / source / age / noContact / callbackOverdue /
 *     linkPending are now validated server-side query params
 *     (lib/lead-list/lead-list-contract.ts -- THE contract home);
 *   - the "No contact logged" OD-3 predicate's ONE home moved to that
 *     module (NO_CONTACT_VIEW_TOUCH_EVENT_TYPES);
 *   - the "New" PROXY view (created<7d + untouched) retired in favour of
 *     the honest lifecycle stage filter (stage=new) -- the stage token IS
 *     on the wire since V1-P2;
 *   - "Phone on file" and "Google paid" retired without a successor filter
 *     (recorded in the run record; source=paid_search supersedes the
 *     google_paid intent at the channel grain).
 * Bookmarks never carried the old view state (it was component state), so
 * no URL migration is owed. This module keeps only the still-live pure
 * helpers below.
 *
 * SECURITY POSTURE: every helper here only NARROWS records the backend
 * already scoped to the signed-in user. Nothing in this module is an
 * access decision.
 */

import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import type { DashboardMe } from '@/dashboard/types';
import {
  EMPTY_LEAD_LIST_FILTERS,
  type LeadListFilterState,
} from '@/dashboard/leads/lead-list-contract';
import { SLA_CLASS_ORDER } from '@/dashboard/sla-leakage/sla-leakage-view';

// --- Search (page-local narrowing only; disclosed by the caller) -----

/**
 * Case-insensitive match over the SAFE fields on the wire: lead name,
 * the safe ref ("Lead #36" / "36"), clinic label, and the
 * source/UTM-source tokens. Phone search is IMPOSSIBLE here by design
 * -- no digits or fingerprints ever reach this client. DELIBERATELY
 * CLIENT-side and ephemeral: free text never enters the URL or the API
 * (the C-9 PHI ruling); the page disclosure names the loaded-page scope.
 */
export function leadMatchesQuery(input: Lead360Input, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  const haystack = [
    input.leadName ?? '',
    `lead #${input.safeLeadId}`,
    input.safeLeadId,
    input.clinicLabel ?? '',
    (input.sourceNormalized ?? '').replace(/_/g, ' '),
    input.utmSource ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

// --- M5: terse latest-activity date -----------------------------------

/**
 * Compact "DD Mon" for the terse list activity cell
 * (LEADS-LIST-AEDS-ENTERPRISE-IMPLEMENTATION-V1 M5), derived from the output
 * of the ONE shared IST formatter (`formatTimestampIst` -> "23 Jul 2026,
 * 9:40 AM IST"). Presentation transform only -- NO second date system, no new
 * clock. Returns null for an empty / unparseable instant (the formatter echoes
 * the raw string, which has no leading "DD Mon"), so the caller renders the
 * chip alone rather than a fabricated date. Pure.
 */
export function compactActivityDate(formattedIst: string): string | null {
  const datePart = formattedIst.split(',')[0]?.trim() ?? '';
  const bits = datePart.split(/\s+/);
  if (bits.length < 2) return null;
  const [day, month] = bits;
  // The formatter emits "<day> <Mon> <year>" only for a parsed instant; an
  // unparseable input echoes the raw ISO (no space-separated DD Mon head).
  if (!/^\d{1,2}$/.test(day)) return null;
  if (!/^[A-Za-z]{3,}$/.test(month)) return null;
  return `${day} ${month}`;
}

/**
 * M3 freshness label ("updated X ago") from the last SUCCESSFUL-fetch instant
 * to a "now" instant, both epoch ms. Coarse, honest buckets; a future instant
 * or a sub-minute gap reads "just now". NEVER invents freshness -- the caller
 * passes null (no successful fetch yet) and renders no freshness at all. Pure
 * (the caller owns the clock), so it unit-tests without a real timer.
 */
export function relativeUpdatedLabel(fromMs: number, nowMs: number): string {
  const deltaMin = Math.floor((nowMs - fromMs) / 60000);
  if (deltaMin <= 0) return 'updated just now';
  if (deltaMin === 1) return 'updated 1 min ago';
  if (deltaMin < 60) return `updated ${deltaMin} min ago`;
  const hr = Math.floor(deltaMin / 60);
  if (hr === 1) return 'updated 1 hr ago';
  if (hr < 24) return `updated ${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return days === 1 ? 'updated 1 day ago' : `updated ${days} days ago`;
}

// --- M7: needs-attention strip provenance -----------------------------

/** The urgency tone a needs-attention chip renders in (never color-only --
 *  every chip also carries a text label). */
export type NeedsAttentionTone = 'breach' | 'warm' | 'info';

/**
 * One needs-attention count with FULL provenance. `count` is derived from the
 * loaded, already-fenced records this page received; `filters` is the EXISTING
 * lawful filter state the drill applies (the server stays the fence). Every
 * item names the exact wire field it counts (`sourceField`) so the run-record
 * count-provenance table can cite it.
 */
export interface NeedsAttentionItem {
  readonly key: 'sla_breach' | 'new_leads' | 'unassigned';
  readonly label: string;
  readonly count: number;
  readonly tone: NeedsAttentionTone;
  /** The wire field the count is derived from (provenance). */
  readonly sourceField: string;
  /** The lawful filter state the drill applies (an existing filter). */
  readonly filters: LeadListFilterState;
}

/**
 * Build the needs-attention strip (M7) from the records THIS page already
 * received -- NEVER a new read, never a fabricated or inferred total. Each
 * count cites an exact wire field:
 *   - Breaching SLA: `slaState.breaches.length > 0` (the record-wire SLA seam).
 *   - New leads: `stage === 'new'` (the canonical lifecycle stage token).
 *   - Unassigned: `ownerAssignment.assigned === false` (the ownership overlay;
 *     an absent overlay is NOT counted -- absence is never "unassigned").
 * A count of 0 yields NO item (an omitted chip is preferable to a fabricated
 * one). Every remaining item drills to an EXISTING lawful filter; the server
 * ownership fence re-shapes the drill, so no drill can widen a role's universe
 * (an agent's own leads are all assigned, so `unassigned` is naturally 0 and
 * absent for that role). Counts unsupportable from this wire (callback overdue,
 * appointment today, no-contact) are DEFERRED to the reporting / Action Queue
 * package -- deliberately omitted, never guessed. Pure.
 */
export function buildNeedsAttention(
  records: readonly Lead360Input[],
): readonly NeedsAttentionItem[] {
  const slaBreach = records.filter(
    (r) => r.slaState !== undefined && r.slaState.breaches.length > 0,
  ).length;
  const newLeads = records.filter((r) => r.stage === 'new').length;
  const unassigned = records.filter(
    (r) => r.ownerAssignment?.assigned === false,
  ).length;

  const items: NeedsAttentionItem[] = [];
  if (slaBreach > 0) {
    items.push({
      key: 'sla_breach',
      label: 'Breaching SLA',
      count: slaBreach,
      tone: 'breach',
      sourceField: 'slaState.breaches',
      filters: { ...EMPTY_LEAD_LIST_FILTERS, slaClasses: SLA_CLASS_ORDER },
    });
  }
  if (newLeads > 0) {
    items.push({
      key: 'new_leads',
      label: 'New leads',
      count: newLeads,
      tone: 'info',
      sourceField: 'stage',
      filters: { ...EMPTY_LEAD_LIST_FILTERS, stages: ['new'] },
    });
  }
  if (unassigned > 0) {
    items.push({
      key: 'unassigned',
      label: 'Unassigned',
      count: unassigned,
      tone: 'warm',
      sourceField: 'ownerAssignment.assigned',
      filters: { ...EMPTY_LEAD_LIST_FILTERS, assignee: 'unassigned' },
    });
  }
  return items;
}

// --- Context bar + routing helpers -----------------------------------

/** Operator-facing role labels for the workspace context bar. COMPLETE over
 *  the role union -- an unmapped role previously leaked its raw token
 *  (CALL_CENTER_AGENT) into the UI (ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1
 *  audit S2; presentation copy only, no authority meaning). */
export const WORKSPACE_ROLE_LABELS: Readonly<
  Partial<Record<DashboardMe['role'], string>>
> = {
  owner: 'Owner',
  coc: 'COC',
  receptionist: 'Front desk',
  call_center_agent: 'Call center agent',
  doctor: 'Doctor',
  regional_head: 'Regional head',
  gm_agm: 'GM / AGM',
  patient: 'Patient',
};

export function workspaceRoleLabel(role: DashboardMe['role']): string {
  return WORKSPACE_ROLE_LABELS[role] ?? role;
}

/** Sentinel for the "all assigned clinics" picker option ('' in the URL). */
export const ALL_CLINICS_VALUE = '';

/** Detail href within the workspace (keeps the clinic narrowing). */
export function workspaceLeadDetailHref(
  safeLeadId: string,
  clinicId: number | string | null,
): string {
  const base = `/dashboard/leads/${encodeURIComponent(safeLeadId)}`;
  if (clinicId === null || String(clinicId).trim() === '') return base;
  return `${base}?clinicId=${encodeURIComponent(String(clinicId))}`;
}

/**
 * Pure access decision for the workspace route guard. Mirrors
 * `decideNavSectionAccess` (role-view.ts) exactly, with the section
 * check replaced by the server-computed `leadsWorkspaceEnabled`
 * signal: ABSENT means disabled (fail-closed) — the flag is off, or
 * the role is outside the v1 audience. Backend route auth remains the
 * security boundary; this only closes the UX gap.
 */
export type LeadsWorkspaceAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

export function decideLeadsWorkspaceAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): LeadsWorkspaceAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (state.me.role === 'receptionist' || state.me.leadsWorkspaceEnabled !== true) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}
